// =============================================================================
// ui/state/useDuplicateScan.ts — "is the same trade in this account twice?"
// -----------------------------------------------------------------------------
// A HOOK, not a provider: one surface asks the question, and the answer is only
// worth having on the screen that can act on it.
//
// ── ⚠ IT FAILS QUIET, AND THAT IS THE RIGHT DIRECTION HERE ──
//
// A failed scan leaves the banner absent. The alternative — an error strip on
// the Accounts tab saying the duplicate scan could not run — puts a warning in
// front of a trader about a tool they were not looking for, on a screen whose
// job is to show them what they are running. The cost of silence is that a
// duplicate goes unnoticed for one more visit; the cost of noise is a tab that
// looks broken every time the network hiccups.
//
// ⚠ THE TOOL ITSELF DOES NOT FAIL QUIET. Once the trader opens it, every error
// is on screen — see `DedupeTradesModal`. Silence is only acceptable while
// nobody has asked.
// =============================================================================

import { useCallback, useEffect, useState } from 'react';
import { useSupabase } from './supabase';
import { loadDuplicateSignatures } from '../../io/sync/duplicateScan';
import {
  detectDuplicateTrades, duplicateExcess,
  type DupGroup, type DupSigTrade,
} from '../../selectors/duplicateTrades';

export interface DuplicateScanState {
  /**
   * Every trade's signature fields, as read.
   *
   * ⚠ EXPOSED SO THE ACCOUNT MERGE CAN USE THE SAME READ. Both tools need the
   * same nine columns over the whole book — the trade tool to group them, the
   * merge to count trades per account and to decide which of a losing copy's
   * trades the keeper already has. Two hooks would mean two whole-book reads on
   * every visit to this tab, for one banner each.
   */
  rows: readonly DupSigTrade[];
  /** Candidate groups, newest read. Empty until a read succeeds. */
  groups: readonly DupGroup<DupSigTrade>[];
  /** How many rows would go if every group were accepted. */
  excess: number;
  scanning: boolean;
  /** Re-run after a removal, so the banner reflects what is left. */
  rescan: () => void;
}

export function useDuplicateScan(): DuplicateScanState {
  const { sb } = useSupabase();
  const [rows, setRows] = useState<readonly DupSigTrade[]>([]);
  const [groups, setGroups] = useState<readonly DupGroup<DupSigTrade>[]>([]);
  const [scanning, setScanning] = useState(true);
  const [nonce, setNonce] = useState(0);

  const rescan = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setScanning(true);
    void (async () => {
      try {
        const read = await loadDuplicateSignatures(sb);
        if (cancelled) return;
        setRows(read);
        // ⚠ The demo book is skipped inside the detector, not here — its rows
        // are generated and can repeat by construction, and that is a fact
        // about the data rather than about this screen.
        setGroups(detectDuplicateTrades(read));
      } catch {
        if (!cancelled) { setRows([]); setGroups([]); }
      } finally {
        if (!cancelled) setScanning(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sb, nonce]);

  return { rows, groups, excess: duplicateExcess(groups), scanning, rescan };
}
